import { MIN_DEPOSIT_AMOUNT, RESTRICTED_MIN_DEPOSIT_AMOUNT } from "@constants/chart";
import { CUTOFF_MINUTES, ET_TIMEZONE, WEEKDAY_NAMES } from "@constants/sip";
import { AccountType, SipFrequency } from "@ctypes";
import { EasternTimeParts, SipStartInfoKey, SipYmd } from "@ctypes/sip";

const FIVE_TWO_NINE_PRODUCT_ACCOUNT_TYPES: ReadonlyArray<AccountType> = [
  AccountType.FiveTwoNinePlan,
  AccountType.Individual_529,
];

export function getMinDepositAmount(accountType?: AccountType | null): number {
  if (accountType && FIVE_TWO_NINE_PRODUCT_ACCOUNT_TYPES.includes(accountType)) {
    return RESTRICTED_MIN_DEPOSIT_AMOUNT;
  }

  return MIN_DEPOSIT_AMOUNT;
}

/** SIP recurring schedule is monthly-only for 529 and Junior Roth products. */
export function isRestrictedToMonthlySipAccountType(accountType?: AccountType | null): boolean {
  return accountType != null && FIVE_TWO_NINE_PRODUCT_ACCOUNT_TYPES.includes(accountType);
}

export function getAvailableFrequencies(
  allFrequencies: SipFrequency[],
  accountType?: AccountType | null,
): SipFrequency[] {
  if (!isRestrictedToMonthlySipAccountType(accountType)) {
    return allFrequencies;
  }

  return allFrequencies.filter((f) => f === SipFrequency.Monthly);
}

export function getSipStartInfoKey(frequency: SipFrequency): SipStartInfoKey | null {
  if (frequency !== SipFrequency.Weekly && frequency !== SipFrequency.Monthly) {
    return null;
  }

  const now = new Date();
  const etParts = getEasternTimeParts(now);

  if (frequency === SipFrequency.Weekly) {
    return getWeeklySipStartInfoKey(etParts);
  }

  return getMonthlySipStartInfoKey(etParts);
}

function getEasternTimeParts(date: Date): EasternTimeParts {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TIMEZONE,
    weekday: "long",
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const map: Record<string, string> = {};

  for (const part of parts) {
    if (part.type !== "literal") {
      map[part.type] = part.value;
    }
  }

  const weekdayName = map.weekday ?? "Monday";
  const weekday = WEEKDAY_NAMES[weekdayName] ?? 1;
  const day = parseInt(map.day ?? "1", 10);
  const month = parseInt(map.month ?? "1", 10);
  const year = parseInt(map.year ?? String(new Date().getFullYear()), 10);
  const hour = parseInt(map.hour ?? "0", 10);
  const minute = parseInt(map.minute ?? "0", 10);

  return { weekday, day, month, year, hour, minute };
}

function getWeeklySipStartInfoKey(
  et: EasternTimeParts,
): SipStartInfoKey.WeeklyStartsToday | SipStartInfoKey.WeeklyNextMonday {
  const timeMinutes = et.hour * 60 + et.minute;
  const isStartsToday =
    (et.weekday === 1 && timeMinutes >= CUTOFF_MINUTES) ||
    (et.weekday >= 2 && et.weekday <= 4) ||
    (et.weekday === 5 && timeMinutes < CUTOFF_MINUTES);

  return isStartsToday ? SipStartInfoKey.WeeklyStartsToday : SipStartInfoKey.WeeklyNextMonday;
}

function getMonthlySipStartInfoKey(
  et: EasternTimeParts,
): SipStartInfoKey.MonthlyStartsToday | SipStartInfoKey.MonthlyNextFirst {
  const nextFirst = getNextFirstOfMonth(et);
  const cutoffDate = addDays(nextFirst, -3);

  const nowTimeMinutes = et.hour * 60 + et.minute;
  const nowYmd: SipYmd = {
    year: et.year,
    month: et.month,
    day: et.day,
  };

  const cmpCutoff = compareYmd(nowYmd, cutoffDate);
  const cmpNextFirst = compareYmd(nowYmd, nextFirst);

  let inNextBusinessDayWindow: boolean;

  if (cmpCutoff < 0) {
    inNextBusinessDayWindow = false;
  } else if (cmpNextFirst > 0) {
    inNextBusinessDayWindow = false;
  } else if (cmpCutoff === 0) {
    inNextBusinessDayWindow = nowTimeMinutes >= CUTOFF_MINUTES;
  } else if (cmpNextFirst === 0) {
    inNextBusinessDayWindow = nowTimeMinutes < CUTOFF_MINUTES;
  } else {
    inNextBusinessDayWindow = true;
  }

  return inNextBusinessDayWindow
    ? SipStartInfoKey.MonthlyNextFirst
    : SipStartInfoKey.MonthlyStartsToday;
}

function compareYmd(a: SipYmd, b: SipYmd): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

function getNextFirstOfMonth(et: EasternTimeParts): SipYmd {
  if (et.day === 1 && et.hour * 60 + et.minute < CUTOFF_MINUTES) {
    return { year: et.year, month: et.month, day: 1 };
  }
  if (et.month === 12) {
    return { year: et.year + 1, month: 1, day: 1 };
  }
  return { year: et.year, month: et.month + 1, day: 1 };
}

function addDays(ymd: SipYmd, days: number): SipYmd {
  const d = new Date(ymd.year, ymd.month - 1, ymd.day + days);
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  };
}
